import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Container,
  Paper,
} from '@mui/material';
import { auth } from '../../services/api';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const VerifyEmail: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        if (!token) {
          setStatus('error');
          setMessage('Verification token is missing');
          return;
        }

        const response = await auth.verifyEmail(token);
        
        if (response.message) {
          setStatus('success');
          setMessage(response.message);
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error: any) {
        setStatus('error');
        setMessage(error.message || 'An error occurred during verification');
      }
    };

    verifyEmail();
  }, [location]);

  const handleNavigate = () => {
    navigate('/login');
  };

  return (
    <Container maxWidth="sm">
      <Paper 
        elevation={3}
        sx={{ 
          p: 4, 
          mt: 8,
          borderRadius: 2,
          textAlign: 'center'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Typography component="h1" variant="h5">
            Email Verification
          </Typography>

          {status === 'loading' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress />
              <Typography color="text.secondary">
                Verifying your email address...
              </Typography>
            </Box>
          )}

          {status === 'success' && (
            <>
              <CheckCircleOutlineIcon 
                color="success" 
                sx={{ fontSize: 64 }} 
              />
              <Typography variant="h6" color="success.main">
                Verification Successful!
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {message}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNavigate}
                fullWidth
              >
                Proceed to Login
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <ErrorOutlineIcon 
                color="error" 
                sx={{ fontSize: 64 }} 
              />
              <Typography variant="h6" color="error">
                Verification Failed
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {message}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNavigate}
                fullWidth
              >
                Go to Login
              </Button>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default VerifyEmail; 